/**
 * Phase 3 convergence anchor — the ONE shared derivation of a task answer's
 * durable per-message-doc key.
 *
 * Kept in its own ZERO-dependency module (no Firestore, no firebase/auth, no
 * React) so the pure reducer/persist hot paths — `interruptedTurn.ts`,
 * `streamFinalizePersist.ts` — can freeze the anchor without dragging the
 * firebase SDK into their import graph. `chatThreadMessageDocFirestore.ts`
 * re-exports it so existing call sites are unchanged.
 */

/**
 * The stable per-doc key for a server-backed answer: `prest0n_task_<serverTaskId>`.
 * Returns null when there is no server task (a plain message keys by its own id).
 * This exact format is what the server stamps as `recoveredMessageId`, so the
 * client and server writers converge on a single Firestore document.
 */
export function deriveTaskAnchorId(serverTaskId: string | null | undefined): string | null {
  const t = typeof serverTaskId === 'string' ? serverTaskId.trim() : '';
  return t ? `prest0n_task_${t}` : null;
}

/**
 * The shared-store id of the HUMAN turn that opened a task:
 * `prest0n_task_<taskId>__user` — the client twin of the server's
 * `userTurnAnchorId` (functions/src/prest0nSession/groupThreadMessageDoc.ts),
 * parity-pinned by tests/prest0nSession/groupThreadMessageDoc.parity.test.ts.
 * Minted on the client BEFORE the bubble exists (PR-2, 2026-09-08) so the
 * delivery ledger, the group-store echo and the server doc all name one row.
 */
export function userTurnAnchorId(taskId: string | null | undefined): string | null {
  const t = typeof taskId === 'string' ? taskId.trim() : '';
  return t ? `prest0n_task_${t}__user` : null;
}

/**
 * The send instant encoded in a client-minted task id (`task_<base36 ms>_<rand>`,
 * see mintClientTaskId). Null for any other shape — callers fall back to now.
 */
export function orderTsFromClientTaskId(taskId: string | null | undefined): number | null {
  const t = typeof taskId === 'string' ? taskId.trim() : '';
  const m = /^task_([0-9a-z]+)_[0-9a-z]+$/i.exec(t);
  if (!m) return null;
  const ms = Number.parseInt(m[1], 36);
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

/**
 * A task mint time is only a mint time inside this window — the same
 * plausibility bounds the server's decoders use
 * (taskQueue.deterministicMessageAnchorForTask,
 * groupThreadDurableWrite.liveVmAnchorTsFromTaskId). A base36 segment that is
 * not a timestamp ("task_abc_xyz" → 13368ms → 1970) must NEVER become a sort
 * position, or one malformed id drags a message to the top of a transcript
 * forever.
 */
const TASK_MINT_MIN_PLAUSIBLE_MS = Date.UTC(2020, 0, 1);
const TASK_MINT_MAX_PLAUSIBLE_MS = Date.UTC(2100, 0, 1);

/**
 * The turn's mint instant recovered from a shared-store message's OWN doc id —
 * `prest0n_task_task_<base36 ms>_<rand>` for an answer, the same plus `__user`
 * for the question that opened it. Null for every other id shape (migration
 * copies keyed by content hash, agent notes like `triage_report_<id>`), whose
 * stored `orderTs` stays authoritative.
 *
 * WHY A READER DERIVES THIS AT ALL (2026-09-09, Malaika's report "sequence is
 * changed"): both docs of a turn are written by more than one writer, and until
 * that day the accept-time human write stamped `orderTs` from the SERVER's
 * receive clock while every terminal writer stamped the mint time — so on a
 * fast turn the question sorted BELOW its own answer for every participant but
 * the asker. The writer is fixed, but the docs already written keep their
 * skewed stamps, and no sweep rewrites a transcript. The id carries the truth,
 * so deriving it here repairs every existing thread at read time with no
 * migration and no writes — and makes the transcript immune to the next writer
 * that skews a stamp.
 *
 * Both docs of a pair yield the SAME value, which is the point: `seq`
 * (question 0, answer 1) then decides, exactly as the two stamps tying was
 * always meant to make it.
 */
export function mintTsFromGroupMessageId(messageId: string | null | undefined): number | null {
  const id = typeof messageId === 'string' ? messageId.trim() : '';
  const m = /^prest0n_task_(task_[0-9a-z]+_[0-9a-z]+)(?:__user)?$/i.exec(id);
  if (!m) return null;
  const ms = orderTsFromClientTaskId(m[1]);
  if (ms === null) return null;
  return ms >= TASK_MINT_MIN_PLAUSIBLE_MS && ms < TASK_MINT_MAX_PLAUSIBLE_MS ? ms : null;
}

/**
 * The sort position for one shared-store message: its own id's mint time when
 * the id carries one, else the stored `orderTs`. Pure and per-document, so the
 * comparators that use it stay a valid total order (a pairwise "same turn ⇒
 * compare by seq" rule is NOT transitive and would make Array.sort's result
 * undefined — see the ordering test's transitivity pin).
 */
export function canonicalGroupOrderTs(message: {
  id?: unknown;
  messageId?: unknown;
  orderTs?: unknown;
}): number | undefined {
  const stored = typeof message?.orderTs === 'number' && Number.isFinite(message.orderTs)
    ? message.orderTs
    : undefined;
  const id = typeof message?.messageId === 'string' && message.messageId.trim()
    ? message.messageId
    : (typeof message?.id === 'string' ? message.id : '');
  return mintTsFromGroupMessageId(id) ?? stored;
}
